/**
 * Small icon set used across the dashboard. Single source of truth for stroke
 * weight, size and viewBox so icons look identical wherever they're used.
 */

interface IconProps {
  className?: string;
  size?: number;
  strokeWidth?: number;
}

function defaults({ className, size = 18, strokeWidth = 2 }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    className,
  };
}

export function IconDashboard(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  );
}

export function IconAccounts(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
    </svg>
  );
}

export function IconCampaigns(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M3 11l18-8-8 18-2-8-8-2z" />
    </svg>
  );
}

export function IconDM(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M21 12a8.5 8.5 0 1 1-3.5-6.9L21 4l-1.1 3.5A8.5 8.5 0 0 1 21 12z" />
      <path d="M8 11h.01M12 11h.01M16 11h.01" />
    </svg>
  );
}

export function IconActivity(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <polyline points="3 12 7 12 9 17 13 6 16 14 18 12 21 12" />
    </svg>
  );
}

export function IconSettings(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

export function IconEye(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function IconHeart(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

export function IconUserPlus(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  );
}

export function IconUserMinus(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  );
}

export function IconChat(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export function IconComment(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function IconUsers(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export function IconArrowRight(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

export function IconArrowUp(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

export function IconClose(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function IconPlus(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

export function IconTrash(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function IconPencil(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

export function IconClock(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export function IconTarget(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  );
}

export function IconRepeat(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

export function IconCheck(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function IconAlert(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

export function IconBolt(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

export function IconHash(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" />
      <line x1="16" y1="3" x2="14" y2="21" />
    </svg>
  );
}

export function IconAt(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <circle cx="12" cy="12" r="4" />
      <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" />
    </svg>
  );
}

export function IconSignOut(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

export function IconMenu(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

export function IconShield(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

export function IconSearch(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export function IconFilter(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

export function IconCalendar(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

export function IconLock(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export function IconGlobe(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

export function IconCrown(props: IconProps = {}): JSX.Element {
  return (
    <svg {...defaults(props)}>
      <path d="M3 17l3-9 6 6 6-6 3 9z" />
      <path d="M3 21h18" />
    </svg>
  );
}
